/**
 * McpToolRegisterForm — "Register Tool" (F032 MCP Governance).
 *
 * Registers a tool exposed by an already-registered MCP server, so the control plane can assess
 * its risk (write/delete/code-execution capability, data sensitivity) and gate it behind approval
 * when warranted. `POST /api/portal/mcp-governance/tools`.
 *
 * `mcpServerId` is a fixed prop (the server the caller is currently viewing) — not a
 * user-editable field, matching the "one form type per API command" rule (ADR-013): this form
 * always registers a tool on a specific, already-selected server. It is therefore excluded from
 * the Zod schema (`McpToolRegister.schema.ts`) and attached to the payload after validation.
 *
 * Pattern: `react-hook-form` + `zodResolver` (Charter M3). The outer `Dialog` chrome is owned by
 * `McpGovernancePage.tsx` (`src/pages/**`) — this component renders the form CONTENT only and
 * keeps its existing `onSuccess`/`onCancel`/`onDirtyChange`/`mcpServerId` prop contract so the
 * hosting page needs no change.
 */
import { useState } from 'react';
import type { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dropdown, Field, Input, Option, Switch, Textarea, makeStyles, tokens } from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';
import { useMutation } from '@tanstack/react-query';
import { mcpGovernanceApi } from '../../../services/api';
import type { McpTool, McpToolRegistrationInput } from '../../../types';
import { applyServerErrors, useReportFormDirty, FormActions, ServerErrorBanner } from '../../../components/forms';
import { DATA_SENSITIVITY_OPTIONS } from './mcpServerFormFields';
import {
  McpToolRegisterSchema,
  type McpToolRegisterFormValues,
  type McpToolRegisterFormOutput,
} from './McpToolRegister.schema';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

// Known request-DTO field keys — used by the server-error -> field mapping (B-03/A-01).
const KNOWN_FIELDS = [
  'mcpServerId',
  'toolName',
  'description',
  'dataAccessLevel',
  'inputSchemaJson',
  'outputSchemaJson',
  'metadataJson',
] as const;

const DEFAULT_VALUES: McpToolRegisterFormValues = {
  toolName: '',
  description: '',
  dataAccessLevel: 'None',
  canWrite: false,
  canDelete: false,
  canExecuteCode: false,
  requiresApproval: false,
  inputSchemaJson: '',
  outputSchemaJson: '',
  metadataJson: '',
};

export interface McpToolRegisterFormProps {
  /** The MCP server this tool is registered against — fixed for the lifetime of the form. */
  mcpServerId: number;
  onSuccess?: (tool: McpTool) => void;
  onCancel?: () => void;
  /** Reports dirty state up so the hosting dialog can guard an unsaved-changes dismiss. */
  onDirtyChange?: (dirty: boolean) => void;
}

export const McpToolRegisterForm: FC<McpToolRegisterFormProps> = ({
  mcpServerId,
  onSuccess,
  onCancel,
  onDirtyChange,
}) => {
  const styles = useStyles();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<McpToolRegisterFormValues, unknown, McpToolRegisterFormOutput>({
    resolver: zodResolver(McpToolRegisterSchema),
    mode: 'onTouched',
    defaultValues: DEFAULT_VALUES,
  });

  useReportFormDirty(isDirty, onDirtyChange);

  const canExecuteCode = watch('canExecuteCode');
  const dataAccessLevel = watch('dataAccessLevel');

  const [globalError, setGlobalError] = useState<string | undefined>();

  const registerMutation = useMutation({
    mutationFn: (input: McpToolRegistrationInput) => mcpGovernanceApi.registerTool(input),
    onSuccess: (tool) => {
      reset(DEFAULT_VALUES);
      onSuccess?.(tool);
    },
    onError: (error) => {
      const { globalMessage } = applyServerErrors(error, KNOWN_FIELDS, setError);
      setGlobalError(globalMessage);
    },
  });

  // C-01 — high-risk advisory: code-execution capability OR a Restricted/Pii data tier drives
  // the tool to High-risk or above under the risk model.
  const isHighRisk = canExecuteCode || dataAccessLevel === 'Restricted' || dataAccessLevel === 'Pii';

  const onValid = (values: McpToolRegisterFormOutput) => {
    // Guard double-submit.
    if (registerMutation.isPending) return;
    setGlobalError(undefined);

    const input: McpToolRegistrationInput = {
      mcpServerId,
      toolName: values.toolName,
      description: values.description,
      inputSchemaJson: values.inputSchemaJson,
      outputSchemaJson: values.outputSchemaJson,
      dataAccessLevel: values.dataAccessLevel,
      canWrite: values.canWrite,
      canDelete: values.canDelete,
      canExecuteCode: values.canExecuteCode,
      requiresApproval: values.requiresApproval,
      metadataJson: values.metadataJson,
    };

    registerMutation.mutate(input);
  };

  return (
    <form onSubmit={handleSubmit(onValid)} className={styles.form} noValidate>
      <ServerErrorBanner title="Unable to Register Tool" message={globalError} testId="mcp-tool-form-server-error" />

      {isHighRisk && (
        <ServerErrorBanner
          title="Elevated Capability"
          message="This tool will be classified High-risk or above."
          testId="mcp-tool-form-high-risk-warning"
          intent="warning"
        />
      )}

      <Field
        label="Tool Name"
        required
        validationState={errors.toolName ? 'error' : undefined}
        validationMessage={errors.toolName?.message}
      >
        <Controller
          name="toolName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="mcp-tool-form-toolName"
              value={field.value ?? ''}
              placeholder="Human-readable tool name"
              disabled={registerMutation.isPending}
              maxLength={200}
            />
          )}
        />
      </Field>

      <Field
        label="Description"
        validationState={errors.description ? 'error' : undefined}
        validationMessage={errors.description?.message}
      >
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              data-testid="mcp-tool-form-description"
              value={field.value ?? ''}
              placeholder="What this tool does (optional)"
              disabled={registerMutation.isPending}
              resize="vertical"
            />
          )}
        />
      </Field>

      <Field
        label="Data Access Level"
        required
        validationState={errors.dataAccessLevel ? 'error' : undefined}
        validationMessage={errors.dataAccessLevel?.message}
      >
        <Controller
          name="dataAccessLevel"
          control={control}
          render={({ field }) => (
            <Dropdown
              value={field.value}
              selectedOptions={[field.value]}
              disabled={registerMutation.isPending}
              onOptionSelect={(_, data) => field.onChange(data.optionValue ?? field.value)}
              onBlur={field.onBlur}
              data-testid="mcp-tool-form-dataAccessLevel"
            >
              {DATA_SENSITIVITY_OPTIONS.map((v) => (
                <Option key={v} value={v}>
                  {v}
                </Option>
              ))}
            </Dropdown>
          )}
        />
      </Field>

      <Field label="Can Write" hint="Tool can create or modify data">
        <Controller
          name="canWrite"
          control={control}
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={(_, data) => field.onChange(data.checked)}
              disabled={registerMutation.isPending}
              label={field.value ? 'Yes' : 'No'}
              data-testid="mcp-tool-form-canWrite"
            />
          )}
        />
      </Field>

      <Field label="Can Delete" hint="Tool can delete data">
        <Controller
          name="canDelete"
          control={control}
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={(_, data) => field.onChange(data.checked)}
              disabled={registerMutation.isPending}
              label={field.value ? 'Yes' : 'No'}
              data-testid="mcp-tool-form-canDelete"
            />
          )}
        />
      </Field>

      <Field label="Can Execute Code" hint="Tool can run arbitrary code">
        <Controller
          name="canExecuteCode"
          control={control}
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={(_, data) => field.onChange(data.checked)}
              disabled={registerMutation.isPending}
              label={field.value ? 'Yes' : 'No'}
              data-testid="mcp-tool-form-canExecuteCode"
            />
          )}
        />
      </Field>

      <Field label="Requires Approval" hint="Tool must be explicitly approved before use">
        <Controller
          name="requiresApproval"
          control={control}
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={(_, data) => field.onChange(data.checked)}
              disabled={registerMutation.isPending}
              label={field.value ? 'Required' : 'Not Required'}
              data-testid="mcp-tool-form-requiresApproval"
            />
          )}
        />
      </Field>

      <Field
        label="Input Schema (JSON)"
        validationState={errors.inputSchemaJson ? 'error' : undefined}
        validationMessage={errors.inputSchemaJson?.message}
      >
        <Controller
          name="inputSchemaJson"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              data-testid="mcp-tool-form-inputSchemaJson"
              value={field.value ?? ''}
              placeholder="Optional JSON Schema for the tool's input"
              disabled={registerMutation.isPending}
              resize="vertical"
            />
          )}
        />
      </Field>

      <Field
        label="Output Schema (JSON)"
        validationState={errors.outputSchemaJson ? 'error' : undefined}
        validationMessage={errors.outputSchemaJson?.message}
      >
        <Controller
          name="outputSchemaJson"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              data-testid="mcp-tool-form-outputSchemaJson"
              value={field.value ?? ''}
              placeholder="Optional JSON Schema for the tool's output"
              disabled={registerMutation.isPending}
              resize="vertical"
            />
          )}
        />
      </Field>

      <Field
        label="Metadata (JSON)"
        validationState={errors.metadataJson ? 'error' : undefined}
        validationMessage={errors.metadataJson?.message}
      >
        <Controller
          name="metadataJson"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              data-testid="mcp-tool-form-metadataJson"
              value={field.value ?? ''}
              placeholder="Optional JSON metadata"
              disabled={registerMutation.isPending}
              resize="vertical"
            />
          )}
        />
      </Field>

      <FormActions
        mode="create"
        testIdPrefix="mcp-tool-form"
        isSubmitting={registerMutation.isPending}
        onCancel={onCancel}
        submitLabel="Register Tool"
        submittingLabel="Registering…"
        submitIcon={<AddRegular />}
      />
    </form>
  );
};
